import { z } from "zod";

export interface Item {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description: string | null;
  unitCost: string;
  accountCode: string | null;
  taxType: string | null;
  isActive: boolean | null;
  xeroItemId: string | null;
  xeroSyncedAt: Date | string | null;
  xeroSyncStatus: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export const insertItemSchema = z.object({
  tenantId: z.string().optional(),
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  unitCost: z.string().optional(),
  accountCode: z.string().optional(),
  taxType: z.string().optional(),
  isActive: z.boolean().optional(),
});

export type InsertItem = z.infer<typeof insertItemSchema>;

export interface Payment {
  id: string;
  tenantId: string;
  customerId: string | null;
  quoteId: string | null;
  invoiceId: string | null;
  jobId: string | null;
  stripePaymentIntentId: string;
  stripeChargeId: string | null;
  paymentType: string;
  amount: string;
  currency: string;
  status: string;
  captureMethod: string | null;
  paymentLinkUrl: string | null;
  metadata: unknown | null;
  errorCode: string | null;
  failureMessage: string | null;
  receivedAt: Date | null;
  settledAt: Date | null;
  refundedAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface Settings {
  id: string;
  tenantId: string;
  businessName: string | null;
  businessEmail: string | null;
  businessPhone: string | null;
  businessAddress: string | null;
  businessAbn: string | null;
  businessLogo: string | null;
  logoUrl: string | null;
  taxRate: string | null;
  currency: string | null;
  timezone: string | null;
  invoicePrefix: string | null;
  quotePrefix: string | null;
  jobPrefix: string | null;
  defaultPaymentTerms: number | null;
  quoteValidityDays: number | null;
  emailFooter: string | null;
  smsSignature: string | null;
  smsSender: string | null;
  emailFrom: string | null;
  abn: string | null;
  licenseNumber: string | null;
  bankName: string | null;
  bankBsb: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  payId: string | null;
  invoiceNotes: string | null;
  invoiceTerms: string | null;
  quoteNotes: string | null;
  quoteTerms: string | null;
  termsAndConditions: string | null;
  termsAndConditionsFileUrl: string | null;
  reviewLink: string | null;
  googleReviewUrl: string | null;
  depositRequired: boolean | null;
  defaultDepositPercent: string | null;
  depositPercentage: string | null;
  defaultMarkupPercent: string | null;
  applyMarkupByDefault: boolean | null;
  showDepositBlock: boolean | null;
  defaultDepositText: string | null;
  defaultQuoteIntro: string | null;
  defaultTerms: string | null;
  gstMode: string | null;
  allowLineGstOverride: boolean | null;
  stripeSuccessUrl: string | null;
  stripeCancelUrl: string | null;
  xeroConnected: boolean | null;
  stripeConnected: boolean | null;
  mapDefaultLat: string | null;
  mapDefaultLng: string | null;
  weeklyIncomeGoal: string | null;
  financialYearTarget: string | null;
  calendarBlockMode: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface CalendarEvent {
  id: string;
  tenantId: string;
  jobId: string | null;
  title: string;
  description: string | null;
  startTime: Date;
  endTime: Date;
  allDay: boolean | null;
  location: string | null;
  assignedTo: string[] | null;
  eventType: string | null;
  externalId: string | null;
  source: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface Notification {
  id: string;
  tenantId: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean | null;
  createdAt: Date | null;
}
